import { useEffect, useState } from "react";
import SimuladoService from "../../sdk/services/SimuladoService";
import { Simulado } from "../../sdk/@types";
import useSessionKey from "./useSessionKey";

/**
 * O próximo simulado disponível para o usuário logado: o menor `start`
 * **futuro** entre os simulados com `situation === "disponivel"`, ou `null`
 * quando não há nenhum (ou enquanto a leitura da sessão atual não voltou).
 *
 * ⚠️ O filtro de futuro existe desde a primeira versão deste hook só na
 * intenção (tasks.md 1.1 pedia "future-start simulado"); a implementação
 * original filtrava por `situation === "disponivel" && s.start` sem checar a
 * data, então um `disponivel` com `start` no passado "ganhava" de um
 * genuinamente futuro por ter `start` menor. Achado por `topbar-validity-and-
 * simulado-pills` ao reusar o hook: a Topbar só pode mostrar a pill quando o
 * próximo simulado está no futuro, e um guard local no consumidor não
 * resolve — ele só sabe descartar o valor único que o hook devolveu, não
 * escolher o próximo da lista. O filtro mora aqui para que `Home.view.tsx`
 * ganhe a mesma correção.
 *
 * `GET /mocktests/allbyuser` é cacheado por sessão, não por hook — mesmo
 * espírito de `loggedUser.ts`/`useDueCount`: duas montagens deste hook na
 * mesma página (hoje o card do Home, depois também a Topbar) compartilham a
 * mesma promise em voo, então a segunda nunca dispara uma segunda chamada.
 * Token novo (troca de sessão na mesma aba) invalida o cache e relê. Fora da
 * troca de sessão, `invalidateSimuladosCache()` (abaixo) é a única outra
 * forma de derrubar o cache — de propósito sem TTL: um cache por tempo
 * trocaria um bug determinístico ("mente até a próxima montagem") por uma
 * janela em que ele ainda mente (reinscricao-apos-cancelamento, design.md,
 * Decisão 3).
 */

// Varredura de `src/` por consumidores de `GET /mocktests/allbyuser`
// (reinscricao-apos-cancelamento, tasks.md 3.3): a rota só é alcançada via
// `SimuladoService.getSimuladosDoUsuario()`, e essa chamada tem exatamente
// dois lugares no código — só os dois leitores já conhecidos:
// `useSimulado.fetchSimuladosDoUsuario` (store zustand, consumida por
// `InscricaoSimulado.view.tsx`) e o `fetchSimulados` abaixo, deste hook.
// Nenhum terceiro consumidor encontrado.
let cache: { session: string; simulados: Promise<Simulado.Simulado[]> } | null = null;

/**
 * Zera o cache de módulo, para que a próxima montagem deste hook, na mesma
 * sessão, releia em vez de devolver a lista estale. Chamada de um lugar só:
 * depois de uma inscrição bem sucedida em `InscricaoSimulado.view.tsx`
 * (reinscricao-apos-cancelamento, design.md, Decisão 3) — é o que faz o
 * card de próximo simulado do `/home` e a pill da Topbar deixarem de
 * contradizer a tela em que o aluno acabou de se inscrever.
 */
export function invalidateSimuladosCache(): void {
  cache = null;
}

function fetchSimulados(session: string): Promise<Simulado.Simulado[]> {
  if (cache?.session === session) return cache.simulados;

  const simulados = SimuladoService.getSimuladosDoUsuario().catch((raw) => {
    if (cache?.session === session) cache = null;
    throw raw;
  });

  cache = { session, simulados };
  return simulados;
}

function pickNextSimulado(simulados: Simulado.Simulado[]): Simulado.Simulado | null {
  const now = Date.now();
  return (
    simulados
      .filter((s) => s.situation === "disponivel" && s.start && new Date(s.start).getTime() > now)
      .sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime())[0] ?? null
  );
}

export default function useNextSimulado(): Simulado.Simulado | null {
  const session = useSessionKey();
  const [state, setState] = useState<{ session: string | null; next: Simulado.Simulado | null }>({
    session: null,
    next: null,
  });

  useEffect(() => {
    if (!session) return;

    fetchSimulados(session)
      .then((res) => setState({ session, next: res ? pickNextSimulado(res) : null }))
      .catch(() => setState({ session, next: null }));
  }, [session]);

  return state.session === session ? state.next : null;
}
